// Specs of the archetype-1 (summary) documents: Ringkasan, Performa Kelas,
// Performa Instruktur, Performa Paket / Membership, Pendapatan Paket /
// Membership (+ Promo), Bandingkan. Pure (see ./shared.ts).
import { translate, translateWithVars } from "shared/languages";
import { formatDateOrDash, formatDateRangeOrDash, formatMoneyOrDash } from "shared/reports/columnCatalogue";
import {
  ClassAnalytics,
  InstructorAnalytics,
  PackageAnalyticsOverview,
  SalesTrendAnalytics,
} from "shared/types";
import {
  formatCurrency,
  formatUpgrades,
  productStatusChip,
} from "shared/utils";
import { countPromoProducts } from "shared/utils/promo-count";
import { hbarChartHeight } from "../../chartGeometry";
import { CHART_W_HALF, DocSpec, lineHeights, notePt, summaryChartRowPt } from "../../pagePlan";
import { joinNotes, taxBasisNote, truncationNote } from "../../reportText";
import { SortMeta } from "../../sort";
import { ColumnDef, columns, pct, productNoun, productNounPlural } from "./shared";

type ProductType = "paket" | "membership";

// A summary document is normally the complete table, so its note only ever
// says anything when the admin's row choice cut it short.
const summaryNote = (shown: number, total: number, noun: string) =>
  truncationNote({ shown, total, noun, loaded: total, sortedOffDefault: false });

// ── Ringkasan Pendapatan ────────────────────────────────────────────────────

export type TrendRow = SalesTrendAnalytics["chart"][number];

export const trendTotalRow = (data: SalesTrendAnalytics): TrendRow => {
  const totalLabel = translate("analyticsPage.class.total_label");
  return data.chart.reduce(
    (acc, m) => ({
      month: totalLabel,
      month_label: totalLabel,
      cash_revenue: acc.cash_revenue + m.cash_revenue,
      paket_revenue: acc.paket_revenue + m.paket_revenue,
      membership_revenue: acc.membership_revenue + m.membership_revenue,
      earned_revenue: acc.earned_revenue + m.earned_revenue,
      open_sessions_completed: acc.open_sessions_completed + m.open_sessions_completed,
      private_sessions_completed: acc.private_sessions_completed + m.private_sessions_completed,
    }),
    {
      month: totalLabel,
      month_label: totalLabel,
      cash_revenue: 0,
      paket_revenue: 0,
      membership_revenue: 0,
      earned_revenue: 0,
      open_sessions_completed: 0,
      private_sessions_completed: 0,
    },
  );
};

export const trendSpec: DocSpec<SalesTrendAnalytics, TrendRow> = {
  layout: "summary",
  limited: false,
  meta: () => ({}),
  table: (data) => data.chart,
  columns: () =>
    columns<TrendRow>([
      { metric: "month", render: (r) => r.month_label },
      { metric: "total_sales", render: (r) => formatCurrency(r.cash_revenue) },
      { metric: "package_sales", render: (r) => formatCurrency(r.paket_revenue) },
      { metric: "membership_sales", render: (r) => formatCurrency(r.membership_revenue) },
      { metric: "booking_revenue", render: (r) => formatCurrency(r.earned_revenue) },
      { metric: "open_sessions", render: (r) => String(r.open_sessions_completed) },
      { metric: "private_sessions", render: (r) => String(r.private_sessions_completed) },
    ]),
  kpis: () => [],
  // Two charts side by side, both chartH tall.
  summaryPt: (_data, block, _m, chartH) => summaryChartRowPt(block, chartH),
  note: (data) => taxBasisNote(data, "earned_cash"),
  totalRow: trendTotalRow,
};

// ── Performa Kelas ──────────────────────────────────────────────────────────

type KelasRow = ClassAnalytics["table"][number];

const KELAS_META = (): SortMeta => ({
  sessions_completed: { label: translate("analyticsPage.columns.sessions_completed"), type: "number" },
  unique_clients: { label: translate("analyticsPage.columns.unique_clients"), type: "number" },
  new_clients: { label: translate("analyticsPage.columns.new_clients"), type: "number" },
  // Mirrors the UI's fillRateSortValue: private rows sort below any open value.
  fill_rate: {
    label: translate("analyticsPage.columns.fill_rate"),
    type: "number",
    value: (r: KelasRow) => (r.type === "private" ? -1 : r.fill_rate),
  },
  revenue: { label: translate("analyticsPage.columns.booking_revenue"), type: "number" },
});

export const kelasSpec: DocSpec<ClassAnalytics, KelasRow> = {
  layout: "detail",
  limited: true,
  meta: KELAS_META,
  table: (data) => data.table,
  columns: (data) =>
    columns<KelasRow>([
      { metric: "class", render: (r) => r.name },
      {
        metric: "class_type",
        render: (r) => (r.type === "open" ? "Open" : r.type === "private" ? "Private" : r.type),
      },
      { metric: "sessions_completed", render: (r) => String(r.sessions_completed) },
      { metric: "unique_clients", render: (r) => String(r.unique_clients) },
      { metric: "new_clients", render: (r) => String(r.new_clients) },
      { metric: "fill_rate", render: (r) => (r.type === "private" ? "-" : pct(r.fill_rate)) },
      // Pembatasan Admin: dropped for a financially restricted Admin, exactly
      // as the screen drops it, so the PDF prints what that admin can see.
      // The row picker plans pages from this same spec, so its count stays
      // right for the narrower table.
      ...(data.money_hidden
        ? []
        : [{ metric: "booking_revenue" as const, render: (r: KelasRow) => formatMoneyOrDash(r.revenue) }]),
    ]),
  kpis: (data) => [
    { value: String(data.open_sessions.value), label: translate("analyticsPage.class.kpi_open_sessions") },
    { value: String(data.private_sessions.value), label: translate("analyticsPage.class.kpi_private_sessions") },
    { value: pct(data.avg_fill_rate.value), label: translate("analyticsPage.class.kpi_fill_rate") },
  ],
  // The session split bar between the strip and the table.
  extrasPt: (_, block) => block.barsRow,
  defaultSort: { key: "sessions_completed", order: "descend" },
  note: (data, shown) =>
    joinNotes(
      summaryNote(shown, data.table.length, translate("pdf.noun.kelas")),
      taxBasisNote(data, "earned"),
    ),
};

// ── Performa Instruktur ─────────────────────────────────────────────────────

type InstrukturRow = InstructorAnalytics["table"][number];

const INSTRUKTUR_META = (): SortMeta => ({
  session_contribution_pct: { label: translate("analyticsPage.columns.session_contribution"), type: "number" },
  sessions_completed: { label: translate("analyticsPage.columns.sessions_completed"), type: "number" },
  bookings: { label: translate("analyticsPage.columns.bookings"), type: "number" },
  unique_clients: { label: translate("analyticsPage.columns.unique_clients"), type: "number" },
  open_sessions: { label: translate("analyticsPage.columns.open_sessions"), type: "number" },
  private_sessions: { label: translate("analyticsPage.columns.private_sessions"), type: "number" },
});

export const instrukturSpec: DocSpec<InstructorAnalytics, InstrukturRow> = {
  layout: "detail",
  limited: true,
  meta: INSTRUKTUR_META,
  table: (data) => data.table,
  columns: () =>
    columns<InstrukturRow>([
      { metric: "instructor", render: (r) => r.instructor_name },
      { metric: "session_contribution", render: (r) => pct(r.session_contribution_pct) },
      { metric: "sessions_completed", render: (r) => String(r.sessions_completed) },
      { metric: "open_sessions", render: (r) => String(r.open_sessions) },
      { metric: "private_sessions", render: (r) => String(r.private_sessions) },
      { metric: "bookings", render: (r) => String(r.bookings) },
      { metric: "unique_clients", render: (r) => String(r.unique_clients) },
    ]),
  kpis: (data) => [
    { value: String(data.total_instructors.value), label: translate("analyticsPage.instructor.kpi_total_instructors") },
    { value: String(data.open_sessions.value), label: translate("analyticsPage.instructor.kpi_open_sessions") },
    { value: String(data.private_sessions.value), label: translate("analyticsPage.instructor.kpi_private_sessions") },
  ],
  defaultSort: { key: "session_contribution_pct", order: "descend" },
  note: (data, shown) => summaryNote(shown, data.table.length, translate("pdf.noun.instruktur")),
};

// ── Performa Paket / Membership ─────────────────────────────────────────────

export type ProdukRow = PackageAnalyticsOverview["table"][number];

// The screen's status chip prints as the line under the product's name:
// "Promo · Active / Upcoming / Ended", "Discontinued" for a tier deleted from
// the catalogue, or "Promo · Discontinued" (see productStatusChip).
const productStatusSub = (r: ProdukRow): string | null => productStatusChip(r)?.label ?? null;

const PRODUK_META = (): SortMeta => ({
  active: { label: translate("analyticsPage.columns.active"), type: "number" },
  sold: { label: translate("analyticsPage.columns.sold"), type: "number" },
  credits_remaining: { label: translate("analyticsPage.columns.remaining_credits"), type: "number" },
  repurchase_rate: { label: translate("analyticsPage.columns.repurchase_rate"), type: "number" },
  avg_days_to_exhaust: { label: translate("analyticsPage.columns.avg_days_to_exhaust"), type: "number" },
  avg_visits_per_period: { label: translate("analyticsPage.columns.avg_visits"), type: "number" },
  expiring_soon: { label: translate("analyticsPage.columns.expiring_soon"), type: "number" },
  last_purchase: { label: translate("analyticsPage.columns.last_purchase"), type: "date" },
});

// A null health metric means "no qualifying cohort yet," not zero, the same
// distinction the on-screen table makes.
const pctOrDash = (v: number | null) => (v === null ? "-" : `${v.toFixed(1)}%`);
const daysOrDash = (v: number | null) =>
  v === null ? "-" : `${Math.round(v)} ${translate("class_settings.unit.days")}`;
const countOrDash = (v: number | null) => (v === null ? "-" : v.toFixed(1));
// "Tidak Terbatas" for a membership's credits, as the screen prints.
const creditsOrUnlimited = (v: number | null | undefined) =>
  v === null || v === undefined ? translate("analyticsPage.package.unlimited") : String(v);

export const produkOverviewSpec = (type: ProductType): DocSpec<PackageAnalyticsOverview, ProdukRow> => {
  const isMembership = type === "membership";
  return {
    layout: "detail",
    limited: true,
    meta: PRODUK_META,
    table: (data) => data.table,
    columns: (data) => {
      const defs: ColumnDef<ProdukRow>[] = [
        { metric: isMembership ? "membership" : "package", render: (r) => r.name, sub: productStatusSub },
        { metric: "active", render: (r) => String(r.active) },
        // Bare number, mirroring the screen: no Penjualan column here, so no
        // gap for an Upgrade column to explain.
        { metric: "sold", render: (r) => String(r.sold) },
        // Membership is unlimited by definition; meaningful for Paket only.
        ...(isMembership
          ? []
          : [{ metric: "remaining_credits" as const, render: (r: ProdukRow) => creditsOrUnlimited(r.credits_remaining) }]),
        { metric: "repurchase_rate", render: (r) => pctOrDash(r.repurchase_rate) },
        // Shared copies in brackets, matching the on-screen cell; the word is
        // inside the bracket because print has no tooltip and a bare "(19.0)"
        // reads as a negative number.
        ...(isMembership
          ? [
              {
                metric: "avg_visits" as const,
                render: (r: ProdukRow) =>
                  r.avg_visits_per_period_shared != null
                    ? `${countOrDash(r.avg_visits_per_period)} (${translate("analyticsPage.package.shared_prefix")} ${countOrDash(r.avg_visits_per_period_shared)})`
                    : countOrDash(r.avg_visits_per_period),
              },
            ]
          : [
              {
                metric: "avg_days_to_exhaust" as const,
                render: (r: ProdukRow) =>
                  r.avg_days_to_exhaust_shared != null
                    ? `${daysOrDash(r.avg_days_to_exhaust)} (${translate("analyticsPage.package.shared_prefix")} ${Math.round(r.avg_days_to_exhaust_shared)})`
                    : daysOrDash(r.avg_days_to_exhaust),
              },
            ]),
        {
          metric: "expiring_soon",
          header: translateWithVars("pdf.expiring_col", { days: data.expiring_soon_days }),
          render: (r) => String(r.expiring_soon),
        },
        { metric: "last_purchase", render: (r) => formatDateOrDash(r.last_purchase) },
      ];
      return columns(defs);
    },
    kpis: (data) => {
      const noun = productNoun(type);
      const nouns = productNounPlural(type);
      return [
        { value: String(data.active_packages.value), label: translateWithVars("pdf.active_of", { noun: nouns }) },
        { value: String(data.expired_packages.value), label: translateWithVars("pdf.expired_of", { noun: nouns }) },
        { value: data.best_seller.name ?? "-", label: translateWithVars("pdf.best_seller_of", { noun }) },
      ];
    },
    defaultSort: { key: "active", order: "descend" },
    note: (data, shown) => summaryNote(shown, data.table.length, translate(`pdf.noun.${type}`)),
  };
};

// ── Pendapatan Paket / Membership (+ Promo) ─────────────────────────────────

// The Upgrade entry is gated on the SAME static condition as the column
// itself (paket only), so metadata and column can never drift apart: a sort
// must not point at a column the document does not print.
const revenueMeta = (isMembership: boolean, promo: boolean): SortMeta => ({
  ...(promo
    ? { promo_starts_at: { label: translate("analyticsPage.columns.promo_period"), type: "date" as const } }
    : {}),
  unique_clients: { label: translate("analyticsPage.columns.unique_clients"), type: "number" },
  sold: { label: translate("analyticsPage.columns.sold"), type: "number" },
  ...(isMembership
    ? {}
    : { upgrades: { label: translate("analyticsPage.columns.upgrades"), type: "number" as const } }),
  sales: { label: translate("analyticsPage.columns.sales"), type: "number" },
  credits_used: { label: translate("analyticsPage.columns.credits_used"), type: "number" },
  earned_revenue: { label: translate("analyticsPage.columns.booking_revenue"), type: "number" },
  last_purchase: { label: translate("analyticsPage.columns.last_purchase"), type: "date" },
});

const promoPeriod = (r: ProdukRow) =>
  formatDateRangeOrDash(r.promo_starts_at, r.promo_ends_at);

const revenueColumns = (isMembership: boolean, promo: boolean) =>
  columns<ProdukRow>([
    {
      metric: isMembership ? "membership" : "package",
      render: (r) => r.name,
      sub: productStatusSub,
    },
    ...(promo ? [{ metric: "promo_period" as const, render: promoPeriod }] : []),
    { metric: "unique_clients", render: (r) => String(r.unique_clients) },
    { metric: "sold", render: (r) => String(r.sold) },
    // Always present for paket, a dash where no upgrade landed in this
    // period. Their fee sits in Penjualan on the same row.
    ...(isMembership ? [] : [{ metric: "upgrades" as const, render: (r: ProdukRow) => formatUpgrades(r.upgrades) }]),
    { metric: "sales", render: (r) => formatCurrency(r.sales) },
    // Paket only: Membership has no credit-consumption concept. Paired on
    // purpose: Kredit Terpakai is the raw count, Pendapatan dari Booking its
    // rupiah value.
    ...(isMembership
      ? []
      : [
          { metric: "credits_used" as const, render: (r: ProdukRow) => String(r.credits_used) },
          { metric: "booking_revenue" as const, render: (r: ProdukRow) => formatCurrency(r.earned_revenue) },
        ]),
    { metric: "last_purchase", render: (r) => formatDateOrDash(r.last_purchase) },
  ]);

export const pendapatanProdukSpec = (type: ProductType): DocSpec<PackageAnalyticsOverview, ProdukRow> => {
  const isMembership = type === "membership";
  return {
    layout: "detail",
    limited: true,
    meta: () => revenueMeta(isMembership, false),
    table: (data) => data.table,
    columns: () => revenueColumns(isMembership, false),
    kpis: (data) => {
      const totalSold = data.table.reduce((sum, r) => sum + r.sold, 0);
      return [
        { value: formatCurrency(data.total_sales.value), label: translate("analyticsPage.sales.kpi_total_sales") },
        { value: String(totalSold), label: translateWithVars("pdf.sold_kpi", { noun: productNounPlural(type) }) },
        {
          value: data.best_seller_by_revenue.name ?? "-",
          // The screen card's own key, so the two surfaces cannot drift.
          label: translate(
            `analyticsPage.${type === "membership" ? "membership" : "package"}.kpi_purchases_best_seller`,
          ),
        },
      ];
    },
    defaultSort: { key: "sales", order: "descend" },
    note: (data, shown) =>
      joinNotes(
        summaryNote(shown, data.table.length, translate(`pdf.noun.${type}`)),
        taxBasisNote(data, type === "membership" ? "cash" : "earned_cash"),
      ),
  };
};

export const pendapatanPromoSpec = (type: ProductType): DocSpec<PackageAnalyticsOverview, ProdukRow> => {
  const isMembership = type === "membership";
  return {
    layout: "detail",
    limited: true,
    meta: () => revenueMeta(isMembership, true),
    table: (data) => data.table,
    columns: () => revenueColumns(isMembership, true),
    kpis: (data) => {
      return [
        // The screen card's own label; the page uses the `package` namespace for both types.
        { value: String(countPromoProducts(data.table)), label: translate("analyticsPage.package.kpi_promo_total") },
        {
          value: String(countPromoProducts(data.table, { activeOnly: true })),
          label: translate("analyticsPage.package.kpi_promo_active"),
        },
        {
          value: data.best_seller_by_revenue.name ?? "-",
          label: translate("analyticsPage.package.kpi_promo_best_seller_revenue"),
        },
      ];
    },
    defaultSort: { key: "promo_starts_at", order: "descend" },
    note: (data, shown) =>
      joinNotes(
        summaryNote(
          shown,
          data.table.length,
          `${translate("pdf.noun.promo")} ${translate(`pdf.noun.${type}`)}`,
        ),
        taxBasisNote(data, type === "membership" ? "cash" : "earned_cash"),
      ),
  };
};

// ── Bandingkan Paket / Membership ───────────────────────────────────────────

// Same cap as the on-screen charts: a curated comparison past this many
// items is already outside intended use. Only the two charts are capped;
// the table prints every compared item.
export const BANDINGKAN_CHART_MAX_ITEMS = 8;

// A manually curated selection in the order the admin picked: no sort, no
// row picker, every compared item printed.
export const bandingkanSpec = (type: ProductType): DocSpec<PackageAnalyticsOverview, ProdukRow> => {
  const isMembership = type === "membership";
  return {
    layout: "summary",
    limited: false,
    meta: () => ({}),
    table: (data) => data.table,
    columns: () =>
      columns<ProdukRow>([
        {
          metric: isMembership ? "membership" : "package",
          render: (r) => r.name,
          // The compare table mixes promo and regular products, so the chip
          // appears only on promo and discontinued rows, exactly as on screen.
          sub: productStatusSub,
        },
        {
          metric: "promo_period",
          render: (r) => (r.is_promo ? promoPeriod(r) : "-"),
        },
        { metric: "unique_clients", render: (r) => String(r.unique_clients) },
        { metric: "sold", render: (r) => String(r.sold) },
        ...(isMembership ? [] : [{ metric: "upgrades" as const, render: (r: ProdukRow) => formatUpgrades(r.upgrades) }]),
        { metric: "sales", render: (r) => formatMoneyOrDash(r.sales) },
        { metric: "last_purchase", render: (r) => formatDateOrDash(r.last_purchase) },
      ]),
    kpis: () => [],
    // The two bar charts share one height (same labels); the chart-cap note
    // follows when more items were compared than the charts show.
    summaryPt: (data, block, m) => {
      const labels = data.table.slice(0, BANDINGKAN_CHART_MAX_ITEMS).map((r) => r.name);
      const charts = 18 + block.chartHead + hbarChartHeight(labels, CHART_W_HALF);
      const note =
        data.table.length > BANDINGKAN_CHART_MAX_ITEMS
          ? notePt(
              m,
              lineHeights(m),
              translateWithVars("pdf.compare_chart_note", {
                max: BANDINGKAN_CHART_MAX_ITEMS,
                total: data.table.length,
              }),
            )
          : 0;
      return charts + note;
    },
    note: (data) => taxBasisNote(data, "cash"),
  };
};
